<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
const props = defineProps<{
  title: string
  icon: string
  columns: Array<{
    key: string
    label: string
    sortable: boolean
    format?: "number" | "currency"
    align?: "left" | "center" | "right"
    width?: string
  }>
  data: Array<any>
  sortBy?: string
  showIncludeStaff?: boolean
  includeStaff?: boolean
}>();

const emit = defineEmits<{
  sortChange: [sortBy: string]
  includeStaffChange: [value: boolean]
}>();

// Compact number formatter (1.2M, 456K format)
const formatCompactNumber = (value: number): string => {
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(1) + 'M';
  }
  if (value >= 1_000) {
    return (value / 1_000).toFixed(1) + 'K';
  }
  return new Intl.NumberFormat("tr-TR").format(value);
};

// Format value based on column format
const formatValue = (value: any, format?: "number" | "currency") => {
  if (value === null || value === undefined) return "-";

  if (format === "number") {
    return formatCompactNumber(value);
  }

  if (format === "currency") {
    // For currency, use compact format without TL suffix (shown in header)
    return formatCompactNumber(value);
  }

  return value;
};

// Handle include staff checkbox
const handleIncludeStaffChange = (value: boolean | "indeterminate") => {
  if (typeof value === 'boolean') {
    emit('includeStaffChange', value);
  }
};

// Get alignment class
const getAlignClass = (align?: "left" | "center" | "right") => {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
};
</script>

<template>
  <AppCard :title="title" :icon="icon">
    <template v-if="showIncludeStaff" #right>
      <UCheckbox
        :model-value="includeStaff"
        label="Include Staff"
        size="sm"
        :ui="{ label: 'text-xs' }"
        @update:model-value="handleIncludeStaffChange"
      />
    </template>

    <div class="overflow-x-auto">
      <table class="w-full text-xs table-fixed">
        <thead class="border-b-2 border-neutral-200 dark:border-neutral-800">
          <tr class="h-6">
            <th
              v-for="column in columns"
              :key="column.key"
              :class="[
                'py-1 font-medium text-[10px] tracking-wide',
                getAlignClass(column.align)
              ]"
              :style="column.width ? { width: column.width } : undefined"
            >
              <button
                v-if="column.sortable"
                class="hover:underline cursor-pointer inline-flex items-center gap-1"
                @click="emit('sortChange', column.key)"
              >
                <UIcon
                  name="i-lucide-arrow-down"
                  :class="[
                    'w-3 h-3 flex-shrink-0',
                    sortBy === column.key ? 'opacity-100' : 'opacity-0'
                  ]"
                />
                <span>{{ column.label }}</span>
              </button>
              <span v-else>{{ column.label }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in data"
            :key="index"
            class="h-7 border-b border-neutral-100 dark:border-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-900/50 last:border-b-0"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="[
                'py-1',
                getAlignClass(column.align),
                column.key === 'name' ? 'font-medium truncate' : 'tabular-nums'
              ]"
            >
              {{ formatValue(row[column.key], column.format) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </AppCard>
</template>
